import { useCallback, useMemo, useState } from 'react';

import { type ProductFilters } from '../types';

import { getActiveFiltersCount } from '@/modules/search/helpers';

export type ProductFiltersSetters = {
  setSizeFilter: (
    sizesOrUpdater: string[] | ((prevSizes?: string[]) => string[])
  ) => void;
  setColourFilter: (
    coloursOrUpdater: string[] | ((prevColours?: string[]) => string[])
  ) => void;
  setPriceFilter: (minPrice?: number, maxPrice?: number) => void;
  setSaleFilter: (onSale?: boolean) => void;
};

export type UseProductFiltersReturn = ProductFiltersSetters & {
  filters: ProductFilters;
  hasActiveFilters: boolean;
  activeFiltersCount: number;
  clearAll: () => void;
};

export function useProductFilters(): UseProductFiltersReturn {
  const [filters, setFilters] = useState<ProductFilters>({});

  const setSizeFilter = useCallback(
    (sizesOrUpdater: string[] | ((prev: string[] | undefined) => string[])) => {
      setFilters((prev) => {
        const sizes =
          typeof sizesOrUpdater === 'function'
            ? sizesOrUpdater(prev.sizes)
            : sizesOrUpdater;

        return {
          ...prev,
          sizes: sizes.length > 0 ? sizes : undefined,
        };
      });
    },
    []
  );

  const setColourFilter = useCallback(
    (
      coloursOrUpdater: string[] | ((prev: string[] | undefined) => string[])
    ) => {
      setFilters((prev) => {
        const colours =
          typeof coloursOrUpdater === 'function'
            ? coloursOrUpdater(prev.colours)
            : coloursOrUpdater;

        return {
          ...prev,
          colours: colours.length > 0 ? colours : undefined,
        };
      });
    },
    []
  );

  const setPriceFilter = useCallback((min?: number, max?: number) => {
    setFilters((prev) => ({
      ...prev,
      minPrice: min,
      maxPrice: max,
    }));
  }, []);

  const setSaleFilter = useCallback((onSale?: boolean) => {
    setFilters((prev) => ({
      ...prev,
      onSale,
    }));
  }, []);

  const clearAll = useCallback(() => {
    setFilters({});
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined;
    });
  }, [filters]);

  const activeFiltersCount = useMemo(() => {
    return getActiveFiltersCount(filters);
  }, [filters]);

  return {
    filters,
    setSizeFilter,
    setColourFilter,
    setPriceFilter,
    setSaleFilter,
    clearAll,
    hasActiveFilters,
    activeFiltersCount,
  };
}
